import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import LandVision from "@mui/icons-material/Map";
import {
    Button,
    CircularProgress,
    Divider,
    Grid,
    Menu,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { get, isNil, join, map, orderBy } from "lodash";
import numeral from "numeral";
import React, { useState } from "react";
import ReactGA from "react-ga4";
import { Link } from "react-router-dom";
import BSInput from "../../../common/js/components/BSInput";
import BSSelect from "../../../common/js/components/BSSelect";
import DoughnutChart from "../../../common/js/components/DoughnutChart";
import KMStatBox from "../../../common/js/components/KMStatBox";
import KMTools from "../../../common/js/components/KMTools";
import LeaseAndSaleDropdownOptions from "../../../common/js/components/LeaseAndSaleDropdownOptions";
import { Sorty } from "../../../common/js/icons";
import config from "../../../config";
import OutsideCompModal from "../comp/OutsideCompModal";
import RequestModalContainer from "../listing/RequestModalContainer";
import { regions } from "../types/regions";
import { aggregateFromArrayForDisplay, formatDate, toDollarAmount } from "../utils/operations";
import "./dashboard.css";

const StyledPageToolbar = styled(Grid)({
    margin: "35px 0px",
});

const StyledIconButton = styled(LandVision)({
    marginRight: "5px",
});

const StyledPaperSpacing = styled(Paper)({
    padding: "10px 20px",
    marginBottom: "15px",
    fontSize: "105%",
});

const StyledPieSpacing = styled(Paper)({
    padding: "10px 20px",
    fontSize: 14,
    marginBottom: "15px",
});

const StyledPieContainer = styled("div")({
    margin: "20px 0px",
});

const StyledFullWidth = styled(Divider)({
    marginLeft: "-20px",
    marginRight: "-20px",
});

const StyledIdLink = styled(Link)({
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
    color: "#2C99D2",
    fontWeight: 600,
    textDecoration: "none",
});

const StyledLoadingPlaceholder = styled("div")({
    marginTop: "3em",
    marginBottom: "1.5em",
    textAlign: "center",
    width: "100%",
});

const StyledLeftColumn = styled(TableCell)({
    paddingLeft: "0px",
});

const StyledLvButton = styled(Grid)({
    flexGrow: "inherit",
    padding: "8px",
});

const StyledRegionSelector = styled(Grid)({
    flexGrow: "inherit",
    width: "12em",
    marginLeft: "25px",
});

const StyledDateFilterSelector = styled(Grid)({
    flexGrow: "inherit",
    width: "11em",
});

const StyledSortableDiv = styled("div")({
    paddingRight: "5px",
    paddingBottom: "3px",
});

const StyledSortableSpan = styled("span")({
    flexGrow: "inherit",
    width: "11em",
});

const EVPDashboard = (props) => {
    const {
        brokerValues,
        dashboardParams,
        errors,
        evpValues,
        handleDashboardInput,
        handleSort,
        loading,
        searchProperty,
        sort,
        toggleRequestModal,
    } = props;

    const [showModal, setShowModal] = useState(false);
    const [listingDropdown, setListingDropdown] = useState(null);

    const handleShowModal = (spaceType, transactionType) => {
        setShowModal(!showModal);
        toggleRequestModal(spaceType, transactionType);
    };

    const commissionBreakdownByTypeSorted = orderBy(
        get(evpValues, "commissionBreakdownByType", []),
        (x) => {
            return isNil(x.calculatedValueA) ? 0 : x.calculatedValueA;
        },
        "desc"
    );
    const commissionBreakdownByTypeData = map(commissionBreakdownByTypeSorted, (val) => {
        return get(val, "calculatedValueA", 0);
    });
    const commissionBreakdownByTypeLabels = map(commissionBreakdownByTypeSorted, (val) => {
        return get(val, "propertyType", "Unknown");
    });
    const topKmClosedDealsSortColumn = get(sort, "topKmClosedDeals.columnName", "calculatedValueA");
    const topKmClosedDealsSortDirection = get(sort, "topKmClosedDeals.sortDirection", "desc");
    const topKmBrokersSortColumn = get(sort, "topKmBrokers.columnName", "calculatedValueB");
    const topKmBrokersSortDirection = get(sort, "topKmBrokers.sortDirection", "desc");
    const commissionBreakdownByOfficeSortColumn = get(
        sort,
        "commissionBreakdownByOffice.columnName",
        "calculatedValueC"
    );
    const commissionBreakdownByOfficeSortDirection = get(sort, "commissionBreakdownByOffice.sortDirection", "desc");

    const sumDealCount = () => {
        let totalDealCount = 0;
        evpValues.commissionBreakdownByOffice.forEach((office) => {
            totalDealCount = totalDealCount + office.calculatedValueA;
        });
        return totalDealCount;
    };

    const sumTotalCommissionByOffice = () => {
        let totalCommission = 0;
        evpValues.commissionBreakdownByOffice.forEach((office) => {
            totalCommission += office.calculatedValueC;
        });
        return totalCommission;
    };

    const sumTotalConsiderationByOffice = () => {
        let totalConsideration = 0;
        evpValues.commissionBreakdownByOffice.forEach((office) => {
            totalConsideration += office.calculatedValueB;
        });
        return totalConsideration;
    };

    const handleListingDropdown = (event) => {
        setListingDropdown(event.currentTarget.parentElement);
    };

    const handleCloseListingDropdown = () => {
        setListingDropdown(null);
    };

    return (
        <div>
            <StyledPageToolbar container alignItems="center" justifyContent="space-between">
                <Grid item>
                    <h2 className="page-title">Dashboard</h2>
                </Grid>
                <Grid
                    container
                    xs={10}
                    alignItems="center"
                    spacing={0}
                    justifyContent="flex-end"
                    style={{ maxWidth: "90%", flexBasis: "90%" }}
                >
                    <StyledDateFilterSelector item>
                        <BSInput
                            id="startDate"
                            label="Dashboard Date Range"
                            value={get(dashboardParams, "startDate")}
                            type={"date"}
                            style={{ backgroundColor: "white" }}
                            onChange={handleDashboardInput}
                        />
                    </StyledDateFilterSelector>
                    <Grid item align="center">
                        <div style={{ paddingTop: "0px", paddingLeft: "5px", paddingRight: "5px", color: "grey" }}>
                            &mdash;
                        </div>
                    </Grid>
                    <StyledDateFilterSelector item>
                        <BSInput
                            id="endDate"
                            value={get(dashboardParams, "endDate")}
                            type={"date"}
                            onChange={handleDashboardInput}
                        />
                    </StyledDateFilterSelector>
                    <StyledRegionSelector item>
                        <BSSelect
                            id="region"
                            value={get(dashboardParams, "region")}
                            error={get(errors, `dashboardParams.region`, null)}
                            extraInputStyles={{ marginTop: "0px", marginRight: "5px" }}
                            onChange={handleDashboardInput}
                            options={[{ label: "All Regions", value: "all" }, ...regions]}
                        />
                    </StyledRegionSelector>
                    <StyledLvButton item style={{ paddingLeft: "12px" }}>
                        <Button
                            variant="contained"
                            color="primary"
                            href={config.LANDVISION_URL}
                            onClick={() => ReactGA.event("Navigate To Landvision from Dashboard")}
                        >
                            <StyledIconButton /> Go to LandVision
                        </Button>
                    </StyledLvButton>
                    <StyledLvButton item>
                        <Button
                            id="Request-Listing-Dropdown"
                            aria-controls={Boolean(listingDropdown) ? "Request-Listing-Menu" : undefined}
                            aria-haspopup="true"
                            aria-expanded={Boolean(listingDropdown) ? "true" : undefined}
                            variant="contained"
                            onClick={handleListingDropdown}
                            endIcon={<ArrowDropDownIcon />}
                        >
                            Request Listing
                        </Button>
                        <Menu
                            id="Request-Listing-Menu"
                            anchorEl={listingDropdown}
                            open={Boolean(listingDropdown)}
                            onClose={handleCloseListingDropdown}
                            anchorOrigin={{
                                vertical: "bottom", // Align the top of the menu with the bottom of the button
                                horizontal: "right", // Align the right edge of the menu with the right edge of the button
                            }}
                            transformOrigin={{
                                vertical: "top", // Align the bottom of the menu with the top of the button
                                horizontal: "right", // Align the left edge of the menu with the right edge of the button
                            }}
                            sx={{
                                maxHeight: "80vh", // Set a max height for the dropdown
                                overflowY: "none", // Enable vertical scrolling if items exceed max height
                                "& .MuiMenuItem-root": {
                                    paddingRight: "5vw", // Add padding to all MenuItems
                                },
                            }}
                        >
                            <LeaseAndSaleDropdownOptions callback={handleShowModal} />
                        </Menu>
                        <RequestModalContainer
                            {...props}
                            handleShowModal={handleShowModal}
                            showModal={showModal}
                            modalType={"listing"}
                        />
                    </StyledLvButton>
                    <StyledLvButton item className={"dashboard"}>
                        <OutsideCompModal
                            {...props}
                            errors={errors}
                            searchFunction={searchProperty}
                            internal={true}
                            dashboard={true}
                        />
                    </StyledLvButton>
                </Grid>
                <div style={{ display: "flex", gap: "1vw", width: "100%" }}>
                    <div style={{ flex: "3 3 auto" }}>
                        <StyledPaperSpacing>
                            <h3>Commission Breakdown By Office</h3>
                            {loading.commissionBreakdownByOffice ? (
                                <StyledLoadingPlaceholder>
                                    <CircularProgress color="primary" />
                                </StyledLoadingPlaceholder>
                            ) : (
                                <React.Fragment>
                                    <Divider />
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell style={{ width: "20%", padding: 0 }}>
                                                    <div
                                                        style={{ display: "flex", alignItems: "center" }}
                                                        onClick={() =>
                                                            handleSort("commissionBreakdownByOffice", "officeName")
                                                        }
                                                    >
                                                        <StyledSortableSpan>Office</StyledSortableSpan>
                                                        <div>
                                                            <Sorty />
                                                        </div>
                                                    </div>
                                                </TableCell>

                                                <TableCell style={{ width: "20%", padding: 0 }}>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            position: "relative",
                                                        }}
                                                        onClick={() =>
                                                            handleSort(
                                                                "commissionBreakdownByOffice",
                                                                "calculatedValueA"
                                                            )
                                                        }
                                                    >
                                                        <StyledSortableDiv
                                                            style={{ right: "6px", position: "absolute" }}
                                                        >
                                                            Number Of Deals
                                                        </StyledSortableDiv>
                                                        <div style={{ right: "0px", position: "absolute" }}>
                                                            <Sorty />
                                                        </div>
                                                    </div>
                                                </TableCell>

                                                <TableCell numeric style={{ width: "30%", padding: 0 }}>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            position: "relative",
                                                        }}
                                                        onClick={() =>
                                                            handleSort(
                                                                "commissionBreakdownByOffice",
                                                                "calculatedValueB"
                                                            )
                                                        }
                                                    >
                                                        <StyledSortableDiv
                                                            style={{ right: "6px", position: "absolute" }}
                                                        >
                                                            Total Consideration Amt
                                                        </StyledSortableDiv>
                                                        <div style={{ right: "0px", position: "absolute" }}>
                                                            <Sorty />
                                                        </div>
                                                    </div>
                                                </TableCell>

                                                <TableCell style={{ width: "30%", padding: 0 }}>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            position: "relative",
                                                        }}
                                                        onClick={() =>
                                                            handleSort(
                                                                "commissionBreakdownByOffice",
                                                                "calculatedValueC"
                                                            )
                                                        }
                                                    >
                                                        <StyledSortableDiv
                                                            style={{ right: "6px", position: "absolute" }}
                                                        >
                                                            Total KM Commission Amount
                                                        </StyledSortableDiv>
                                                        <div style={{ right: "0px", position: "absolute" }}>
                                                            <Sorty />
                                                        </div>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {orderBy(
                                                evpValues.commissionBreakdownByOffice,
                                                commissionBreakdownByOfficeSortColumn,
                                                commissionBreakdownByOfficeSortDirection
                                            ).map((office, i) => (
                                                <TableRow selectable="false" className="tableRow">
                                                    <StyledLeftColumn style={{ padding: 0 }}>
                                                        {get(office, `officeName`)}
                                                    </StyledLeftColumn>

                                                    {/* number of deals */}
                                                    <TableCell numeric style={{ padding: 0 }}>
                                                        {get(office, `calculatedValueA`)}
                                                    </TableCell>

                                                    {/* total consideration amount */}
                                                    <TableCell numeric style={{ padding: 0 }}>
                                                        {toDollarAmount(get(office, `calculatedValueB`))}
                                                    </TableCell>

                                                    {/* total commission amount */}
                                                    <TableCell numeric style={{ padding: 0 }}>
                                                        {toDollarAmount(get(office, `calculatedValueC`))}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow selectable="false" className="tableRow">
                                                <StyledLeftColumn style={{ fontWeight: "bold", padding: 0 }}>
                                                    Total
                                                </StyledLeftColumn>

                                                {/* number of deals */}
                                                <TableCell numeric style={{ fontWeight: "bold", padding: 0 }}>
                                                    {sumDealCount()}
                                                </TableCell>

                                                {/* total consideration amount */}
                                                <TableCell numeric style={{ fontWeight: "bold", padding: 0 }}>
                                                    {numeral(sumTotalConsiderationByOffice(), 0, "--").format(
                                                        "$ 0,0[.]00"
                                                    )}
                                                </TableCell>

                                                {/* total commission amount */}
                                                <TableCell numeric style={{ fontWeight: "bold", padding: 0 }}>
                                                    {numeral(sumTotalCommissionByOffice(), 0, "--").format(
                                                        "$ 0,0[.]00"
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </React.Fragment>
                            )}
                        </StyledPaperSpacing>
                        <StyledPaperSpacing>
                            <h3>Top Brokers</h3>
                            {loading.topKmBrokers ? (
                                <StyledLoadingPlaceholder>
                                    <CircularProgress color="primary" />
                                </StyledLoadingPlaceholder>
                            ) : (
                                <React.Fragment>
                                    <Divider />
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell style={{ width: "35%", padding: 0 }}>
                                                    <div
                                                        style={{ display: "flex", alignItems: "center" }}
                                                        onClick={() => handleSort("topKmBrokers", "firstName")}
                                                    >
                                                        <StyledSortableSpan>Broker</StyledSortableSpan>
                                                        <div>
                                                            <Sorty />
                                                        </div>
                                                    </div>
                                                </TableCell>

                                                <TableCell style={{ width: "20%", padding: 0 }}>
                                                    <div
                                                        style={{ display: "flex", alignItems: "center" }}
                                                        onClick={() => handleSort("topKmBrokers", "region")}
                                                    >
                                                        <StyledSortableSpan>Region</StyledSortableSpan>
                                                        <div>
                                                            <Sorty />
                                                        </div>
                                                    </div>
                                                </TableCell>

                                                <TableCell style={{ width: "15%", padding: 0 }}>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            position: "relative",
                                                        }}
                                                        onClick={() => handleSort("topKmBrokers", "calculatedValueB")}
                                                    >
                                                        <StyledSortableDiv
                                                            style={{ right: "6px", position: "absolute" }}
                                                        >
                                                            Closed Deals
                                                        </StyledSortableDiv>
                                                        <div style={{ right: "0px", position: "absolute" }}>
                                                            <Sorty />
                                                        </div>
                                                    </div>
                                                </TableCell>

                                                <TableCell style={{ width: "30%", padding: 0 }}>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            position: "relative",
                                                        }}
                                                        onClick={() => handleSort("topKmBrokers", "calculatedValueA")}
                                                    >
                                                        <StyledSortableDiv
                                                            style={{ right: "6px", position: "absolute" }}
                                                        >
                                                            Total Commission Amount
                                                        </StyledSortableDiv>
                                                        <div style={{ right: "0px", position: "absolute" }}>
                                                            <Sorty />
                                                        </div>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {orderBy(
                                                evpValues.topKmBrokers,
                                                topKmBrokersSortColumn,
                                                topKmBrokersSortDirection
                                            ).map((deal, i) => (
                                                <TableRow selectable="false" className="tableRow">
                                                    <StyledLeftColumn style={{ padding: 0 }}>
                                                        {`${get(deal, `firstName`)} ${get(deal, `lastName`)}`}
                                                    </StyledLeftColumn>

                                                    <TableCell style={{ padding: 0 }}>{get(deal, `region`)}</TableCell>

                                                    {/* closed deals */}
                                                    <TableCell numeric style={{ padding: 0 }}>
                                                        {get(deal, `calculatedValueB`)}
                                                    </TableCell>

                                                    {/* total commission amount */}
                                                    <TableCell numeric style={{ padding: 0 }}>
                                                        {toDollarAmount(get(deal, `calculatedValueA`))}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </React.Fragment>
                            )}
                        </StyledPaperSpacing>
                        <StyledPaperSpacing>
                            <h3>Top Closed Deals</h3>
                            {loading.topKmClosedDeals ? (
                                <StyledLoadingPlaceholder>
                                    <CircularProgress color="primary" />
                                </StyledLoadingPlaceholder>
                            ) : (
                                <React.Fragment>
                                    <Divider />
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell style={{ width: "35%", padding: 0 }}>
                                                    <div style={{ display: "flex", alignItems: "center" }}>
                                                        <StyledSortableSpan>Deal</StyledSortableSpan>
                                                    </div>
                                                </TableCell>

                                                <TableCell style={{ width: "15%", padding: 0 }}>
                                                    <div
                                                        style={{ display: "flex", alignItems: "center" }}
                                                        onClick={() => handleSort("topKmClosedDeals", "region")}
                                                    >
                                                        <StyledSortableSpan>Region</StyledSortableSpan>
                                                        <div>
                                                            <Sorty />
                                                        </div>
                                                    </div>
                                                </TableCell>

                                                <TableCell style={{ width: "13%", padding: 0 }}>
                                                    <div style={{ display: "flex", alignItems: "center" }}>
                                                        <StyledSortableSpan>Broker</StyledSortableSpan>
                                                    </div>
                                                </TableCell>

                                                <TableCell style={{ width: "15%", padding: 0 }}>
                                                    <div
                                                        style={{ display: "flex", alignItems: "center" }}
                                                        onClick={() => handleSort("topKmClosedDeals", "batchDate")}
                                                    >
                                                        <StyledSortableSpan>Batch Date</StyledSortableSpan>
                                                        <div>
                                                            <Sorty />
                                                        </div>
                                                    </div>
                                                </TableCell>

                                                <TableCell numeric style={{ width: "15%", padding: 0 }}>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            position: "relative",
                                                        }}
                                                        onClick={() =>
                                                            handleSort("topKmClosedDeals", "calculatedValueA")
                                                        }
                                                    >
                                                        <StyledSortableDiv
                                                            style={{ right: "6px", position: "absolute" }}
                                                        >
                                                            Total KM Commission Amount
                                                        </StyledSortableDiv>
                                                        <div style={{ right: "0px", position: "absolute" }}>
                                                            <Sorty />
                                                        </div>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {orderBy(
                                                evpValues.topKmClosedDeals,
                                                topKmClosedDealsSortColumn,
                                                topKmClosedDealsSortDirection
                                            ).map((deal, i) => (
                                                <TableRow selectable="false" className="tableRow">
                                                    <StyledLeftColumn style={{ padding: 0 }}>
                                                        <StyledIdLink to={`/deal/${get(deal, "dealKey", "")}`}>
                                                            <div>
                                                                {`Deal No. ${get(deal, "dealKey")}`}
                                                                <span className="l" style={{ fontWeight: 200 }}>
                                                                    {get(deal, "listingName", null) !== null
                                                                        ? ` | ${get(deal, "listingName")}`
                                                                        : ""}
                                                                </span>
                                                            </div>
                                                        </StyledIdLink>
                                                        <div>
                                                            {get(deal, "address1")} {get(deal, "address2")}{" "}
                                                            {get(deal, "city")} {get(deal, "state")} {get(deal, "zip")}
                                                        </div>
                                                    </StyledLeftColumn>

                                                    <TableCell style={{ padding: 0 }}>{get(deal, `region`)}</TableCell>

                                                    <TableCell style={{ padding: 0 }}>
                                                        <Tooltip
                                                            id="evp-tooltip"
                                                            placement="top"
                                                            title={join(deal?.calculatedValueE, ", ")}
                                                        >
                                                            <span data-tooltip-id="evp-tooltip">
                                                                {aggregateFromArrayForDisplay(
                                                                    deal?.calculatedValueE,
                                                                    "Brokers"
                                                                )}
                                                            </span>
                                                        </Tooltip>
                                                    </TableCell>

                                                    <TableCell style={{ padding: 0 }}>
                                                        {get(deal, `batchDate`)}
                                                    </TableCell>

                                                    {/* total km commission amount */}
                                                    <TableCell numeric style={{ padding: 0 }}>
                                                        {toDollarAmount(get(deal, `calculatedValueA`))}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </React.Fragment>
                            )}
                        </StyledPaperSpacing>
                    </div>
                    <div>
                        <StyledPieSpacing>
                            <h3>Commission Breakdown By Type</h3>
                            <StyledFullWidth />
                            <StyledPieContainer>
                                <DoughnutChart
                                    commissionBreakdownByTypeData={commissionBreakdownByTypeData}
                                    commissionBreakdownByTypeLabels={commissionBreakdownByTypeLabels}
                                    toDollarAmount={toDollarAmount}
                                />
                            </StyledPieContainer>
                        </StyledPieSpacing>
                        {loading.brokerLicenseExpiration ? (
                            <StyledLoadingPlaceholder>
                                <CircularProgress color="primary" />
                            </StyledLoadingPlaceholder>
                        ) : (
                            <KMStatBox
                                icon={<AccountCircleIcon />}
                                title="License Exp Date"
                                number={formatDate(
                                    "MM/DD/YYYY",
                                    "--"
                                )(get(brokerValues, `brokerLicenseExpiration.expirationDate`, ""))}
                            />
                        )}
                        <KMTools />
                    </div>
                </div>
            </StyledPageToolbar>
        </div>
    );
};

export default EVPDashboard;
